import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import './ToolPage.css'
import './FocusTimer.css'
import { trackEvent } from '../../lib/analytics'
import { useDocumentMeta } from '../../hooks/useDocumentMeta'

const MODE_LABELS = {
  foco: 'Foco',
  pausaCurta: 'Pausa curta',
  pausaLonga: 'Pausa longa',
}

const playBeep = () => {
  const AudioContextClass = window.AudioContext || window.webkitAudioContext
  if (!AudioContextClass) return
  const ctx = new AudioContextClass()
  const oscillator = ctx.createOscillator()
  oscillator.frequency.value = 880
  oscillator.connect(ctx.destination)
  oscillator.start()
  setTimeout(() => {
    oscillator.stop()
    ctx.close()
  }, 400)
}

const formatTime = (totalSeconds) => {
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
}

const FocusTimer = () => {
  useDocumentMeta({
    title: 'Cronômetro Pomodoro / Timer de Foco | jarder.dev',
    description:
      'Ciclos de foco e pausa com aviso sonoro no fim. Grátis, direto no ' +
      'navegador, sem cadastro.',
  })

  const [focusMinutes, setFocusMinutes] = useState('25')
  const [shortBreakMinutes, setShortBreakMinutes] = useState('5')
  const [longBreakMinutes, setLongBreakMinutes] = useState('15')
  const [cyclesUntilLongBreak, setCyclesUntilLongBreak] = useState('4')

  const [mode, setMode] = useState('foco')
  const [secondsLeft, setSecondsLeft] = useState(25 * 60)
  const [running, setRunning] = useState(false)
  const [cyclesCompleted, setCyclesCompleted] = useState(0)
  const originalTitle = useRef(document.title)

  const durationFor = (targetMode) => {
    if (targetMode === 'foco') return Math.max(1, Number(focusMinutes) || 25) * 60
    if (targetMode === 'pausaCurta') return Math.max(1, Number(shortBreakMinutes) || 5) * 60
    return Math.max(1, Number(longBreakMinutes) || 15) * 60
  }

  useEffect(() => {
    if (!running) return undefined
    const intervalId = setInterval(() => {
      setSecondsLeft((current) => current - 1)
    }, 1000)
    return () => clearInterval(intervalId)
  }, [running])

  useEffect(() => {
    if (secondsLeft >= 0) return
    playBeep()
    if (mode === 'foco') {
      const nextCompleted = cyclesCompleted + 1
      setCyclesCompleted(nextCompleted)
      const goesLong = nextCompleted % (Math.max(1, Number(cyclesUntilLongBreak) || 4)) === 0
      const nextMode = goesLong ? 'pausaLonga' : 'pausaCurta'
      setMode(nextMode)
      setSecondsLeft(durationFor(nextMode))
      trackEvent('tool_generate', { tool: 'focus_timer', event: 'foco_completo' })
    } else {
      setMode('foco')
      setSecondsLeft(durationFor('foco'))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secondsLeft])

  useEffect(() => {
    const initialTitle = originalTitle.current
    if (running) {
      document.title = `${formatTime(Math.max(0, secondsLeft))} · ${MODE_LABELS[mode]}`
    } else {
      document.title = initialTitle
    }
    return () => {
      document.title = initialTitle
    }
  }, [secondsLeft, mode, running])

  useEffect(() => {
    if (!running) {
      setSecondsLeft(durationFor(mode))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusMinutes, shortBreakMinutes, longBreakMinutes])

  const start = () => {
    setRunning(true)
    trackEvent('tool_generate', { tool: 'focus_timer', event: 'start' })
  }
  const pause = () => setRunning(false)

  const reset = () => {
    setRunning(false)
    setMode('foco')
    setSecondsLeft(durationFor('foco'))
    setCyclesCompleted(0)
  }

  const skip = () => {
    setSecondsLeft(-1)
  }

  return (
    <div className="tool-page">
      <Link to="/ferramentas" className="tool-back-link">
        ← Todas as ferramentas
      </Link>
      <h1>Cronômetro Pomodoro</h1>
      <p className="tool-disclaimer">
        Ciclos de foco e pausa com aviso sonoro no fim de cada etapa.
        Mantenha a aba aberta para o som e o título da página funcionarem.
      </p>

      <div className="tool-row">
        <div className="tool-field">
          <label htmlFor="focus-minutes">Foco (min)</label>
          <input
            id="focus-minutes"
            type="number"
            min="1"
            value={focusMinutes}
            onChange={(e) => setFocusMinutes(e.target.value)}
            disabled={running}
          />
        </div>
        <div className="tool-field">
          <label htmlFor="short-break-minutes">Pausa curta (min)</label>
          <input
            id="short-break-minutes"
            type="number"
            min="1"
            value={shortBreakMinutes}
            onChange={(e) => setShortBreakMinutes(e.target.value)}
            disabled={running}
          />
        </div>
        <div className="tool-field">
          <label htmlFor="long-break-minutes">Pausa longa (min)</label>
          <input
            id="long-break-minutes"
            type="number"
            min="1"
            value={longBreakMinutes}
            onChange={(e) => setLongBreakMinutes(e.target.value)}
            disabled={running}
          />
        </div>
        <div className="tool-field">
          <label htmlFor="cycles">Ciclos até pausa longa</label>
          <input
            id="cycles"
            type="number"
            min="1"
            value={cyclesUntilLongBreak}
            onChange={(e) => setCyclesUntilLongBreak(e.target.value)}
            disabled={running}
          />
        </div>
      </div>

      <div className="focus-timer-display">
        <span className="focus-timer-mode">{MODE_LABELS[mode]}</span>
        <span className="focus-timer-clock">{formatTime(Math.max(0, secondsLeft))}</span>
        <span className="tool-import-hint">Ciclos de foco completos: {cyclesCompleted}</span>
      </div>

      <div className="tool-result-actions">
        {running ? (
          <button type="button" className="tool-button" onClick={pause}>
            Pausar
          </button>
        ) : (
          <button type="button" className="tool-button" onClick={start}>
            Iniciar
          </button>
        )}
        <button type="button" className="tool-button tool-button-secondary" onClick={skip}>
          Pular etapa
        </button>
        <button type="button" className="tool-button tool-button-secondary" onClick={reset}>
          Reiniciar
        </button>
      </div>

      <div className="tool-info-section">
        <h2>O que é a técnica Pomodoro</h2>
        <p>
          A técnica Pomodoro divide o trabalho em ciclos de foco intenso
          (tradicionalmente 25 minutos) seguidos de pausas curtas, com
          uma pausa mais longa a cada 4 ciclos. A ideia é manter a
          concentração por blocos curtos e previsíveis, evitando fadiga
          mental. Ajuste as durações acima do jeito que funcionar melhor
          para você  o padrão de 25/5/15 é só um ponto de partida.
        </p>
      </div>
    </div>
  )
}

export default FocusTimer
